import React, { useEffect, useState } from 'react';
import { usePos } from '../context/PosContext';
import { useRestaurant } from '../hooks/useRestaurant';
import OrderPanel from '../components/OrderPanel';
import './Sections.css';
import './Restaurant.css';

// Refresh our table lock well within LOCK_STALE_MS so it never ages out mid-edit.
const LOCK_HEARTBEAT_MS = 30000;

// Captain App (captain_app_fragment.xml): the waiter-facing view. Captains pick
// a table and fire KOTs to the kitchen; settlement happens at the till, so the
// panel runs in 'captain' mode (no Settle button). Tables with a pending order
// surface the running total so a captain can keep adding rounds.
const CaptainApp: React.FC = () => {
  const { formatMoney, isOn, servicePoints } = usePos();
  const rest = useRestaurant();
  const [selected, setSelected] = useState<string | null>(null);
  const [premise, setPremise] = useState('');

  // Open a table for editing, taking its lock first so two captains can't edit
  // the same table at once. If another terminal holds it, tell the user instead.
  const openTable = (t: string) => {
    const holder = rest.lockHolder(t);
    if (holder) {
      window.alert(`${t} is being edited on ${holder.deviceName || 'another terminal'}.`);
      return;
    }
    if (!rest.acquireLock(t)) {
      window.alert(`${t} was just opened on another terminal.`);
      return;
    }
    setSelected(t);
  };

  // While a table is open, heartbeat its lock; release it when the panel closes
  // or we navigate away.
  useEffect(() => {
    if (!selected) return;
    const id = setInterval(() => rest.refreshLock(selected), LOCK_HEARTBEAT_MS);
    return () => {
      clearInterval(id);
      rest.releaseLock(selected);
    };
  }, [selected]);

  // "17 — Captain App / C. Captain Wise Premise": a captain works one premise —
  // pick it to filter the table list to tables named under it (prefix match,
  // e.g. premise "Garden" → tables "Garden-1", "Garden-2").
  const premiseWise = isOn('captain_wise_premise');
  const premises = Array.from(
    new Set(servicePoints.map((sp) => sp.premise).filter(Boolean) as string[])
  ).sort();
  const visibleTables =
    premiseWise && premise
      ? rest.tables.filter((t) => t.toLowerCase().startsWith(premise.toLowerCase()))
      : rest.tables;

  const active = rest.orders.filter((o) => o.status === 'running');

  return (
    <div className="section-page rest-page">
      <div className="page-header">
        <h1>CAPTAIN APP</h1>
      </div>

      <div className="section-card">
        <h2>Open a table</h2>
        {premiseWise && premises.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <span className="muted">Premise:</span>
            <select value={premise} onChange={(e) => setPremise(e.target.value)}>
              <option value="">All premises</option>
              {premises.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="tbl-grid">
          {visibleTables.map((t) => {
            const order = rest.runningOrder(t);
            const occupied = !!order;
            const qty = order?.items.reduce((s, i) => s + i.quantity, 0) ?? 0;
            const holder = rest.lockHolder(t);
            return (
              <button
                key={t}
                className={`tbl-card ${occupied ? 'occupied' : 'free'}`}
                onClick={() => openTable(t)}
              >
                <span className="tbl-name">{t}</span>
                <span className="tbl-status">{occupied ? 'Occupied' : 'Free'}</span>
                {occupied && (
                  <span className="tbl-meta">
                    {qty} item{qty === 1 ? '' : 's'}
                  </span>
                )}
                {holder && (
                  <span className="tbl-lock" title={`Editing on ${holder.deviceName || 'another terminal'}`}>
                    🔒 {holder.deviceName || 'in use'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="section-card">
        <h2>Pending orders ({active.length})</h2>
        {active.length === 0 ? (
          <p className="muted">No open tables. Tap a table above to start an order.</p>
        ) : (
          <table className="section-table">
            <thead>
              <tr>
                <th>Table</th>
                <th>Waiter</th>
                <th className="num-cell">Items</th>
                <th className="right">Total</th>
              </tr>
            </thead>
            <tbody>
              {active.map((o) => (
                <tr key={o.id} onClick={() => openTable(o.tableName)} style={{ cursor: 'pointer' }}>
                  <td>{o.tableName}</td>
                  <td>{o.waiter || '—'}</td>
                  <td>{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                  <td className="right">{formatMoney(o.items.reduce((s, i) => s + i.total, 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <OrderPanel
          tableName={selected}
          rest={rest}
          mode="captain"
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default CaptainApp;
